"use client";

import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import NavBar from "../../components/NavBar/NavBar";
import { HGrid, ResponsiveBox } from "../primitives";
import CommonContainerBox from "../../components/custom/containers/CommonContainerBox";
import Header from "../../components/Header/Header";
import ERPSBaseTextField from "../custom/inputs/ERPSBaseTextField";
import ERPSBaseBox from "../custom/inputs/ERPSBaseBox";
import { useSelector, useDispatch } from "react-redux";
import actions from "../../actions";
import { ErrorBoundary } from "react-error-boundary";
import ErrorFallback from "../Error/ErrorFallback";
import Footer from "../Footer/Footer";
import { useMainContentFocus } from "../../hooks";

export const withSidebar = (Component) => (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [financialInstitutionList, setFinancialInstitutionList] = useState([]);

  const roleId = useSelector(
    (state) => state.loginUserReducer.user?.role?.role_id
  );

  const financialInstitutionResponse = useSelector(
    (state) => state.fiReducer?.financialInstitutionList
  );

  const selectedFi = useSelector((state) => state.fiReducer?.selectedFi);
  const loginUser = useSelector((state) => state.loginUserReducer?.user);

  // This logic is used to focus the first interactive element which is loaded only after data returns.
  // This is just a FALLBACK solution. For page with first interactive element from dataload, please use useMainContentFocus directly referencing the logic in PricePlanPendingApprovals.js
  // Using useMainContentFocus directly will able to focus immediately after page load.
  // This fallback perform a focus after 400ms, which the dataload load maybe not completed yet.
  const {
    location: { pathname },
  } = props;
  
  const { mainContentRef } = useMainContentFocus({
    dependencies: [pathname],
    delay: 400,
  });

  useEffect(() => {
    if (!selectedFi && loginUser.fiNumber == 50) {
      dispatch(actions.loginActions.updateUserRolesPermission(50, roleId));
      dispatch(actions.fiActions.getHelpDeskPhoneNumber(50, roleId, t));
    } else if (selectedFi) { // only run if the page load was not caused by handleChange
      dispatch(
        actions.loginActions.updateUserRolesPermission(selectedFi, roleId)
      );
      dispatch(actions.fiActions.getHelpDeskPhoneNumber(selectedFi, roleId, t));
    }
  }, []);

  useEffect(() => {
    if ((!selectedFi && loginUser.fiNumber == 50) || roleId == 6) {
      dispatch(actions.fiActions.getFinancialInstitutionList(t));
    }
  }, []);

  useEffect(() => {
    if (!selectedFi && loginUser.fiNumber == 50) {
      dispatch({ type: "GET_FINANCIAL_INSTITUTION", data: "50" });
    } else {
      dispatch({ type: "GET_FINANCIAL_INSTITUTION", data: selectedFi });
    }
  }, []);

  useEffect(() => {
    if (financialInstitutionResponse && financialInstitutionResponse.length) {
      const data = financialInstitutionResponse.map(
        ({ fi_name, fi_number }) => ({
          value: fi_number,
          label: `${fi_number} - ${fi_name}`,
        })
      );
      const selectedfiName = financialInstitutionResponse.find(
        ({ fi_number }) => fi_number == 50
      );

      const updatedData = data.filter(
        ({ value }) => value !== selectedfiName?.fi_number
      );

      if (selectedfiName) {
        const fiValue = `${selectedfiName.fi_number} - ${selectedfiName.fi_name}`;
        updatedData.unshift({
          value: selectedfiName.fi_number,
          label: fiValue,
        });
      }

      setFinancialInstitutionList(updatedData);
    }
  }, [financialInstitutionResponse]);

  const handleChange = (event) => {
    const fiNumber = event.target.value;
    dispatch({ type: "GET_FINANCIAL_INSTITUTION", data: fiNumber });
    dispatch(
      actions.loginActions.updateUserRolesPermission(fiNumber, roleId, props)
    );
    dispatch(actions.fiActions.getHelpDeskPhoneNumber(fiNumber, roleId, t));
    //sessionStorage.setItem("rolesPermissionUpdated", "true"); // to avoid multiple calls to the backed to get the updated roles permission

  };

  const splittedValue = pathname.split("/");
  const title = splittedValue[splittedValue.length - 1]
    .split("-")
    .map((e) => e.charAt(0).toUpperCase() + e.slice(1))
    .join(" ");
  if (
    props.pageName === "biller-profile" ||
    props.pageName === "download-document"
  ) {
    return (
      <ResponsiveBox>
        {" "}
        <Component {...props} />{" "}
      </ResponsiveBox>
    );
  }

  const hasDropDownDisable = () => {
    const hasEmailAddress = loginUser?.emailAddress;
    // if (loginUser?.changePasswordIndex == "Y") {
    //   return false;
    // } else {
      return Boolean(hasEmailAddress);
    // }
  };

  const logout = () => {
    // dispatch(actions.loginActions.logout({ ...props }));
    // let path = '/';
    // history.push(path);
  };

  return (
    <>
      <Header {...props} />
      <ERPSBaseBox
        mt={8}
        className="sidebar_drop_down_list"
        componentAccess={loginUser?.role?.modules}
        actionType="hasFiManagement"
      >
        <ERPSBaseTextField
          id={`financialInstitution-${selectedFi}`}
          type="text"
          select={true}
          label={t("erps.newuser.label.fi", "Financial Institution")}
          className="three_column__textField"
          value={selectedFi}
          onChange={handleChange}
          size="small"
          required="true"
          items={financialInstitutionList}
          SelectProps={{ native: true }}
          isDisabled={!hasDropDownDisable()}
        />
      </ERPSBaseBox>

      <CommonContainerBox
        title={t("erps.user.pagetitle.message", title)}
        padding="0px 0px 0px 0px"
        minHeight="calc(100vh - 280px)"
        marginTop={"85px"}
        componentAccess={loginUser?.role?.modules}
        actionType="hasFiManagement"
      >
        <HGrid item md={3} lg={3} className="sidebar__disabale">
          <NavBar {...props} />
        </HGrid>
        <HGrid item md={9} lg={9}>
          <ResponsiveBox ref={mainContentRef} maxWidth="100%">
            <ErrorBoundary FallbackComponent={ErrorFallback}>
              <Component {...props} />
            </ErrorBoundary>
          </ResponsiveBox>
        </HGrid>
      </CommonContainerBox>
      <Footer />
    </>
  );
};
